import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Transfer,
  Select,
  Space,
  message,
  Popconfirm,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  UserOutlined,
  SafetyOutlined,
  DownloadOutlined,
  FileTextOutlined,
  UploadOutlined,
  CloudUploadOutlined,
  HistoryOutlined,
  CloudDownloadOutlined,
} from '@ant-design/icons';
import { permissionService } from '../services/permissionService';
import { companyService, Company, buildProjectIdSelectOptions } from '../services/companyService';
import { useI18n } from '../hooks/useI18n';
import { useAuth } from '../contexts/AuthContext';

const { Title, Text } = Typography;

interface Role {
  name: string;
  displayName: string;
  description?: string;
  isSystemRole: boolean;
  isActive: boolean;
}

interface Permission {
  id: number;
  permission_name: string;
  display_name: string;
  description?: string;
  category?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface RolePermissions {
  [roleName: string]: string[];
}

interface TransferItem {
  key: string;
  title: string;
  description?: string;
}

const RolePermissionsManager: React.FC = () => {
  const { t } = useI18n();
  const { isSuperAdmin } = useAuth();
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [rolePermissions, setRolePermissions] = useState<RolePermissions>({});
  const [loading, setLoading] = useState(false);
  const [roleModalVisible, setRoleModalVisible] = useState(false);
  const [editRoleModalVisible, setEditRoleModalVisible] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [roleForm] = Form.useForm();
  const [transferTargetKeys, setTransferTargetKeys] = useState<string[]>([]);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupFiles, setBackupFiles] = useState<any[]>([]);
  const [backupFilesVisible, setBackupFilesVisible] = useState(false);
  const [importing, setImporting] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [backupRemoteLoading, setBackupRemoteLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  /** project 范围：'all' 或 companies.project_id（请求头 X-Project-Id） */
  const [selectedProjectScope, setSelectedProjectScope] = useState<string>('all');
  const [roleSearchKeyword, setRoleSearchKeyword] = useState('');
  const [companies, setCompanies] = useState<Company[]>([]);

  const projectSelectOptions = useMemo(
    () => buildProjectIdSelectOptions(companies, t('common.all')),
    [companies, t]
  );

  useEffect(() => {
    if (selectedProjectScope === 'all') return;
    const ok = projectSelectOptions.some((o) => o.value === selectedProjectScope);
    if (!ok) setSelectedProjectScope('all');
  }, [selectedProjectScope, projectSelectOptions]);

  const loadData = async () => {
    setLoading(true);
    try {
      // 并行加载角色、权限和配置数据
      const [rolesData, permissionsData, configData] = await Promise.all([
        permissionService.getRoles(),
        permissionService.getPermissions(),
        permissionService.getPermissionConfig(selectedProjectScope)
      ]);

      setRoles(rolesData);
      setPermissions(permissionsData);
      setRolePermissions(configData.rolePermissions || {});
    } catch (error) {
      console.error('Failed to load data:', error);
      message.error(t('roles.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedProjectScope]);

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const currentTenant = localStorage.getItem('currentTenant') || 'default';
        const res = await companyService.getCompanies(currentTenant);
        if (res?.success) {
          setCompanies(res.data || []);
          if (selectedProjectScope === 'all' && (res.data || []).length > 0) {
            const firstPid = (res.data as Company[]).find(
              (c) => c.project_id != null && String(c.project_id).trim() !== ''
            )?.project_id;
            if (firstPid != null) setSelectedProjectScope(String(firstPid));
          }
        }
      } catch {
        // 忽略：不影响权限页基本功能
      }
    };
    loadCompanies();
  }, []);

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      setImporting(true);
      const fileContent = await file.text();
      const payload = JSON.parse(fileContent);
      const result = await permissionService.importPermissions(payload);

      if (result?.success) {
        const roleCount = result.data?.roleCount ?? 0;
        const permissionCount = result.data?.permissionCount ?? 0;
        message.success(
          t('roles.importSuccess', { roles: roleCount, permissions: permissionCount })
        );
        loadData();
      } else {
        message.error(result?.message || t('roles.importFailed'));
      }
    } catch (error: any) {
      console.error('Failed to import permissions:', error);
      if (error instanceof SyntaxError) {
        message.error(t('roles.importInvalidFile'));
      } else {
        message.error(error?.response?.data?.message || t('roles.importFailed'));
      }
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  // 创建角色
  const handleCreateRole = () => {
    roleForm.resetFields();
    setRoleModalVisible(true);
  };

  // 编辑角色权限
  const handleEditRole = (role: Role) => {
    setEditingRole(role);
    setTransferTargetKeys(rolePermissions[role.name] || []);
    setEditRoleModalVisible(true);
  };

  // 删除角色
  const handleDeleteRole = async (roleName: string) => {
    try {
      await permissionService.deleteRole(roleName);
      message.success(t('roles.deleteSuccess'));
      loadData();
    } catch (error) {
      console.error('Failed to delete role:', error);
      message.error(t('roles.deleteFailed'));
    }
  };

  // 创建角色提交
  const handleCreateRoleSubmit = async (values: any) => {
    try {
      await permissionService.createRole(values);
      message.success(t('roles.createSuccess'));
      setRoleModalVisible(false);
      loadData();
    } catch (error) {
      console.error('Failed to create role:', error);
      message.error(t('roles.createFailed'));
    }
  };

  // 更新角色权限
  const handleUpdateRolePermissions = async () => {
    if (!editingRole) return;

    try {
      await permissionService.updateRolePermissions(editingRole.name, transferTargetKeys, selectedProjectScope);
      message.success(t('roles.updatePermissionsSuccess'));
      setEditRoleModalVisible(false);
      loadData();
    } catch (error) {
      console.error('Failed to update role permissions:', error);
      message.error(t('roles.updatePermissionsFailed'));
    }
  };

  // 重新加载数据
  const handleReload = async () => {
    try {
      await permissionService.reloadConfig();
      message.success(t('roles.reloadSuccess'));
      loadData();
    } catch (error) {
      console.error('Failed to reload configuration:', error);
      message.error(t('roles.reloadFailed'));
    }
  };

  // 备份权限配置
  const handleBackupPermissions = async () => {
    setBackupLoading(true);
    try {
      const result = await permissionService.exportPermissions();
      if (result.success) {
        message.success(t('roles.backupSuccess', { filename: result.data.filename }));
        if (isSuperAdmin) loadBackupFiles();
      } else {
        message.error(t('roles.backupFailed'));
      }
    } catch (error) {
      console.error('Failed to backup permissions:', error);
      message.error(t('roles.backupFailed'));
    } finally {
      setBackupLoading(false);
    }
  };

  // 加载备份文件列表
  const loadBackupFiles = async () => {
    try {
      const result = await permissionService.getExportFiles();
      if (result.success) {
        setBackupFiles(result.data);
      }
    } catch (error) {
      console.error('Failed to load backup files:', error);
    }
  };

  // 下载权限文件
  const handleDownloadFile = async (filename: string) => {
    try {
      const data = await permissionService.downloadPermissionFile(filename);
      
      // 创建下载链接
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.success(t('roles.fileDownloadSuccess'));
    } catch (error) {
      console.error('Failed to download file:', error);
      message.error(t('roles.fileDownloadFailed'));
    }
  };

  // 显示备份文件列表
  const handleShowBackupFiles = () => {
    setBackupFilesVisible(true);
    loadBackupFiles();
  };

  const handlePublishPermissions = () => {
    Modal.confirm({
      title: t('roles.publishConfirmTitle'),
      content: t('roles.publishConfirmContent'),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        setPublishLoading(true);
        try {
          const result = await permissionService.publishAgentPermissions(selectedProjectScope);
          message.success(result?.message || t('roles.publishSuccess'));
          loadData();
        } catch (error: any) {
          message.error(error?.response?.data?.message || t('roles.publishFailed'));
        } finally {
          setPublishLoading(false);
        }
      }
    });
  };

  const handleRestorePermissions = () => {
    Modal.confirm({
      title: t('roles.restoreConfirmTitle'),
      content: t('roles.restoreConfirmContent'),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        setRestoreLoading(true);
        try {
          const result = await permissionService.restoreAgentPermissions(selectedProjectScope);
          message.success(result?.message || t('roles.restoreSuccess'));
          loadData();
        } catch (error: any) {
          message.error(error?.response?.data?.message || t('roles.restoreFailed'));
        } finally {
          setRestoreLoading(false);
        }
      }
    });
  };

  const handleBackupRemotePermissions = async () => {
    setBackupRemoteLoading(true);
    try {
      const result = await permissionService.backupRemoteAgentPermissions(selectedProjectScope);
      if (result?.success) {
        const filename = result?.data?.filename;
        message.success(
          filename
            ? t('roles.backupRemoteSuccessWithFile', { filename })
            : t('roles.backupRemoteSuccess')
        );
        if (isSuperAdmin) loadBackupFiles();
      } else {
        message.error(result?.message || t('roles.backupRemoteFailed'));
      }
    } catch (error: any) {
      message.error(error?.response?.data?.message || t('roles.backupRemoteFailed'));
    } finally {
      setBackupRemoteLoading(false);
    }
  };

  const transferData: TransferItem[] = permissions.map(permission => ({
    key: permission.permission_name,
    title: permission.display_name,
    description: permission.description || getPermissionDescription(permission.permission_name),
  }));

  const filteredRoles = useMemo(() => {
    const kw = roleSearchKeyword.trim().toLowerCase();
    if (!kw) return roles;
    return roles.filter(
      r =>
        r.name.toLowerCase().includes(kw) ||
        (r.displayName && r.displayName.toLowerCase().includes(kw))
    );
  }, [roles, roleSearchKeyword]);

  // 角色表格列定义
  const roleColumns = [
    {
      title: t('roles.colRoleName'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Role) => (
        <Space>
          <UserOutlined />
          <Text strong>{record.displayName}</Text>
          {record.isSystemRole && <Tag color="red">{t('roles.systemRole')}</Tag>}
        </Space>
      ),
    },
    {
      title: t('roles.colDescription'),
      dataIndex: 'description',
      key: 'description',
      render: (description: string) => (
        <Text type="secondary">{description || t('roles.noDescription')}</Text>
      ),
    },
    {
      title: t('roles.colPermissionCount'),
      key: 'permissionCount',
      render: (_: unknown, record: Role) => {
        const permissionCount = rolePermissions[record.name]?.length || 0;
        return (
          <Tag color={permissionCount > 0 ? 'green' : 'default'}>
            {permissionCount} {t('roles.permissionsUnit')}
          </Tag>
        );
      },
    },
    {
      title: t('roles.colStatus'),
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? t('common.active') : t('common.inactive')}
        </Tag>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_: unknown, record: Role) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditRole(record)}
          >
            {t('roles.editPermissions')}
          </Button>
          {!record.isSystemRole && (
            <Popconfirm
              title={t('roles.confirmDelete')}
              onConfirm={() => handleDeleteRole(record.name)}
              okText={t('common.confirm')}
              cancelText={t('common.cancel')}
            >
              <Button type="link" danger icon={<DeleteOutlined />}> 
                {t('common.delete')}
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // 获取权限描述
  function getPermissionDescription(permissionName: string): string {
    const descriptions: { [key: string]: string } = {
      'admin:roles': t('roles.desc.adminRoles'),
      'admin:system': t('roles.desc.adminSystem'),
      'admin:users': t('roles.desc.adminUsers'),
      
      'edit:all': t('roles.desc.editAll'),
      'edit:sales': t('roles.desc.editSales'),
      'edit:purchase': t('roles.desc.editPurchase'),
      'edit:inventory': t('roles.desc.editInventory'),
      'edit:finance': t('roles.desc.editFinance'),
      
      'view:all': t('roles.desc.viewAll'),
      'view:sales': t('roles.desc.viewSales'),
      'view:purchase': t('roles.desc.viewPurchase'),
      'view:inventory': t('roles.desc.viewInventory'),
      'view:finance': t('roles.desc.viewFinance'),
      
      'MTC_VW_AI_ODLN_HEAD': t('roles.desc.salesDeliveryHead'),
      'MTC_VW_AI_ODLN': t('roles.desc.salesDeliveryLines'),
      'MTC_VW_AI_OINV_HEAD': t('roles.desc.salesInvoiceHead'),
      'MTC_VW_AI_OINV': t('roles.desc.salesInvoiceLines'),
      'MTC_VW_AI_ORDR_HEAD': t('roles.desc.salesOrderHead'),
      'MTC_VW_AI_ORDR': t('roles.desc.salesOrderLines'),
      'MTC_VW_AI_ORIN_HEAD': t('roles.desc.salesReturnHead'),
      'MTC_VW_AI_ORIN': t('roles.desc.salesReturnLines'),
      'MTC_VW_AI_OPKL': t('roles.desc.pickingList'),
      'MTC_VW_AI_ORDN_HEAD': t('roles.desc.debitNoteHead'),
      'MTC_VW_AI_ORDN': t('roles.desc.debitNoteLines'),
      'MTC_VW_AI_CUSTCREDIT': t('roles.desc.customerCredit'),
      'MTC_VW_AI_CUSTBAL': t('roles.desc.customerBalance'),
      'MTC_VW_AI_BATCHSTOCK': t('roles.desc.batchStock'),
      'MTC_VW_AI_OIVL': t('roles.desc.inventoryValuation'),
      'MTC_VW_AI_STOCK': t('roles.desc.stockStatus'),
      'MTC_VW_AI_OIGN_HEAD': t('roles.desc.goodsReceiptHead'),
      'MTC_VW_AI_OIGN': t('roles.desc.goodsReceiptLines'),
      'MTC_VW_AI_OIGE_HEAD': t('roles.desc.goodsIssueHead'),
      'MTC_VW_AI_OIGE': t('roles.desc.goodsIssueLines'),
      'MTC_VW_AI_OWTR_HEAD': t('roles.desc.transferHead'),
      'MTC_VW_AI_OWTR': t('roles.desc.transferLines'),
      'MTC_VW_AI_OWTQ_HEAD': t('roles.desc.transferRequestHead'),
      'MTC_VW_AI_OWTQ': t('roles.desc.transferRequestLines'),
      'MTC_VW_AI_OINC_HEAD': t('roles.desc.inventoryCountingHead'),
      'MTC_VW_AI_OINC': t('roles.desc.inventoryCountingLines'),
      'MTC_VW_AI_OPOR_HEAD': t('roles.desc.purchaseOrderHead'),
      'MTC_VW_AI_OPOR': t('roles.desc.purchaseOrderLines'),
      'MTC_VW_AI_OPDN_HEAD': t('roles.desc.purchaseDeliveryHead'),
      'MTC_VW_AI_OPDN': t('roles.desc.purchaseDeliveryLines'),
      'MTC_VW_AI_OPCH_HEAD': t('roles.desc.purchaseInvoiceHead'),
      'MTC_VW_AI_OPCH': t('roles.desc.purchaseInvoiceLines'),
      'MTC_VW_AI_ORPD_HEAD': t('roles.desc.purchaseReturnHead'),
      'MTC_VW_AI_ORPD': t('roles.desc.purchaseReturnLines'),
      'MTC_VW_AI_ORPC_HEAD': t('roles.desc.purchaseCreditNoteHead'),
      'MTC_VW_AI_OPRC': t('roles.desc.purchaseCreditNoteLines'),
      'MTC_VW_AI_DEACREDIT': t('roles.desc.dealerCreditLimit'),
      'MTC_VW_AI_DEALBAL': t('roles.desc.dealerBalance'),
      'MTC_VW_AI_DEALCREDIT': t('roles.desc.dealerCredit'),
      'MTC_VW_AI_FUNDBAL': t('roles.desc.fundBalance'),
      'MTC_VW_AI_JournalEntry': t('roles.desc.journalEntry'),
      'MTC_VW_AI_OACT': t('roles.desc.chartOfAccounts'),
      'MTC_VW_AI_OCRD': t('roles.desc.businessPartner'),
      'MTC_VW_AI_OWHS': t('roles.desc.warehouse'),
      'MTC_VW_AI_OITM': t('roles.desc.item'),
      'MTC_VW_AI_PurchaseTimelyDelRate': t('roles.desc.purchaseOnTime'),
      'MTC_VW_AI_SalesTimelyDelRate': t('roles.desc.salesOnTime'),
    };
    
    return descriptions[permissionName] || t('roles.desc.default');
  }

  return (
    <div style={{ padding: '24px' }}>
      <input
        type="file"
        accept="application/json"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleImportFileChange}
      />
      <div style={{ marginBottom: '24px' }}>
        <Row align="middle" justify="start" style={{ flexWrap: 'wrap' }}>
          <Space align="baseline" size={16} style={{ flexWrap: 'wrap' }}>
            <Title level={2} style={{ margin: 0 }}>
              <SafetyOutlined style={{ marginRight: '8px' }} />
              {t('roles.title')}
            </Title>
            <Select
              showSearch
              optionFilterProp="label"
              style={{ width: 280 }}
              value={selectedProjectScope}
              onChange={setSelectedProjectScope}
              options={projectSelectOptions}
            />
            <Text type="secondary">Version: —</Text>
            <Text type="secondary">{t('roles.subtitle')}</Text>
            <Space style={{ flexWrap: 'wrap' }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreateRole}
              >
                {t('roles.createRole')}
              </Button>
              <Button
                icon={<UploadOutlined />}
                loading={importing}
                onClick={handleImportClick}
              >
                {t('roles.importPermissions')}
              </Button>
              <Button
                icon={<CloudUploadOutlined />}
                loading={publishLoading}
                onClick={handlePublishPermissions}
              >
                {t('roles.publishPermissions')}
              </Button>
              <Button
                icon={<HistoryOutlined />}
                loading={restoreLoading}
                onClick={handleRestorePermissions}
              >
                {t('roles.restorePermissions')}
              </Button>
              <Button
                icon={<CloudDownloadOutlined />}
                loading={backupRemoteLoading}
                onClick={handleBackupRemotePermissions}
              >
                {t('roles.backupRemotePermissions')}
              </Button>
              <Button
                icon={<DownloadOutlined />}
                loading={backupLoading}
                onClick={handleBackupPermissions}
              >
                {t('roles.backupPermissions')}
              </Button>
              {isSuperAdmin && (
                <Button
                  icon={<FileTextOutlined />}
                  onClick={handleShowBackupFiles}
                >
                  {t('roles.permissionFiles')}
                </Button>
              )}
              <Button
                icon={<ReloadOutlined />}
                onClick={handleReload}
              >
                {t('common.refresh')}
              </Button>
            </Space>
          </Space>
        </Row>
      </div>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={8}>
          <Card>
            <Statistic
              title={t('roles.stat.totalRoles')}
              value={roles.length}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title={t('roles.stat.totalPermissions')}
              value={permissions.length}
              prefix={<SafetyOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title={t('roles.stat.assignedPermissions')}
              value={Object.values(rolePermissions).reduce((sum, perms) => sum + perms.length, 0)}
              prefix={<SafetyOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      <Card style={{ marginBottom: '24px' }}>
        <Input.Search
          allowClear
          placeholder={t('roles.searchRolesPlaceholder')}
          value={roleSearchKeyword}
          onChange={(e) => setRoleSearchKeyword(e.target.value)}
        />
      </Card>

      {/* 角色管理卡片 */}
      <Card title={t('roles.card.roles')} style={{ marginBottom: '24px' }}>
        <Table
          columns={roleColumns}
          dataSource={filteredRoles}
          rowKey="name"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => t('common.paginationTotal', { from: range[0], to: range[1], total }),
          }}
        />
      </Card>

      {/* 创建角色模态框 */}
      <Modal
        title={t('roles.modal.createTitle')}
        open={roleModalVisible}
        onCancel={() => setRoleModalVisible(false)}
        footer={null}
      >
        <Form
          form={roleForm}
          layout="vertical"
          onFinish={handleCreateRoleSubmit}
        >
          <Form.Item
            name="name"
            label={t('roles.roleName')}
            rules={[{ required: true, message: t('roles.enterRoleName') }]}
          >
            <Input placeholder={t('roles.enterRoleName')} />
          </Form.Item>
          <Form.Item
            name="displayName"
            label={t('roles.displayName')}
            rules={[{ required: true, message: t('roles.enterDisplayName') }]}
          >
            <Input placeholder={t('roles.enterDisplayName')} />
          </Form.Item>
          <Form.Item
            name="description"
            label={t('roles.description')}
          >
            <Input.TextArea placeholder={t('roles.enterDescription')} rows={3} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {t('common.create')}
              </Button>
              <Button onClick={() => setRoleModalVisible(false)}>
                {t('common.cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑角色权限模态框 */}
      <Modal
        title={t('roles.modal.editPermissionsTitle')}
        open={editRoleModalVisible}
        onCancel={() => setEditRoleModalVisible(false)}
        onOk={handleUpdateRolePermissions}
        width={800}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>{t('roles.roleLabel')}</Text>
          <Tag color="blue" style={{ marginLeft: 8 }}>
            {editingRole?.displayName}
          </Tag>
        </div>
        
        <Transfer
          dataSource={transferData}
          targetKeys={transferTargetKeys}
          onChange={(targetKeys, direction, moveKeys) => {
            setTransferTargetKeys(targetKeys as string[]);
          }}
          disabled={false}
          render={(item) => (
            <div style={{ padding: '8px 12px' }}>
              <div style={{ fontWeight: 500, marginBottom: '4px' }}>{item.title}</div>
              {item.description && (
                <div style={{ fontSize: '12px', color: '#666', lineHeight: '1.3' }}>
                  {item.description}
                </div>
              )}
            </div>
          )}
          titles={[t('roles.transfer.available'), t('roles.transfer.assigned')]}
          showSearch
          showSelectAll={false}
          filterOption={(inputValue, option) => {
            const lowerInput = inputValue.toLowerCase();
            return option.title.toLowerCase().includes(lowerInput) ||
              (option.description?.toLowerCase().includes(lowerInput) ?? false);
          }}
          listStyle={{
            width: '45%',
            height: 400,
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
          }}
          operations={[t('roles.transfer.add'), t('roles.transfer.remove')]}
          oneWay={false}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
          }}
        />
      </Modal>

      {/* 导出文件列表模态框（仅超级管理员，对应 GET .../export/files） */}
      {isSuperAdmin && (
        <Modal
          title={t('roles.backupFileList')}
          open={backupFilesVisible}
          onCancel={() => setBackupFilesVisible(false)}
          footer={[
            <Button key="close" onClick={() => setBackupFilesVisible(false)}>
              {t('common.close')}
            </Button>
          ]}
          width={800}
        >
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">{t('roles.backupFileListDesc')}</Text>
          </div>

          {backupFiles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
              <FileTextOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
              <div>{t('roles.noBackupFiles')}</div>
            </div>
          ) : (
            <Table
              dataSource={backupFiles}
              rowKey="filename"
              pagination={false}
              size="small"
              columns={[
                {
                  title: t('roles.fileName'),
                  dataIndex: 'filename',
                  key: 'filename',
                  render: (filename: string) => (
                    <Space>
                      <FileTextOutlined />
                      <Text code>{filename}</Text>
                    </Space>
                  ),
                },
                {
                  title: t('roles.fileSize'),
                  dataIndex: 'size',
                  key: 'size',
                  render: (size: number) => (
                    <Text type="secondary">{(size / 1024).toFixed(1)} KB</Text>
                  ),
                },
                {
                  title: t('roles.createdAt'),
                  dataIndex: 'created',
                  key: 'created',
                  render: (created: string) => (
                    <Text type="secondary">
                      {new Date(created).toLocaleString('zh-CN')}
                    </Text>
                  ),
                },
                {
                  title: t('common.actions'),
                  key: 'actions',
                  render: (_: unknown, record: any) => (
                    <Button
                      type="link"
                      icon={<DownloadOutlined />}
                      onClick={() => handleDownloadFile(record.filename)}
                    >
                      {t('common.download')}
                    </Button>
                  ),
                },
              ]}
            />
          )}
        </Modal>
      )}
    </div>
  );
};

export default RolePermissionsManager;
